import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAppId } from '../context/AppIdContext';
import { useLocation, useNavigate } from 'react-router-dom';
import '../style/speakSearch.css';
import Header from '../component/header';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const MyGroup = () => {
    // --- State ---
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [cardSearchTerm, setCardSearchTerm] = useState("");

    // --- Context ---
    const { appId, language, userId } = useAppId();
    const lang = language || 'en';

    // --- Translation Dictionary ---
    const t = {
        en: {
            pageTitle: "My Group",
            pageSubtitle: "List of members assigned to your personal group.",
            filterPlaceholder: "Filter group members by name...",
            loadingData: "Loading Data...",
            noMembers: "No members found in your group.",
            noMatching: "No matching names found.",
            fatherHusband: "F/S :",
            serialNo: "Sr No. :",
            mobile: "Mobile :",
            idCardNo: "ID Card No :",
            boothNo: "Booth No :",
            houseNo: "House No :",
            pollingLoc: "Voting Loc :",
            call: "Call",
            text: "Text",
            slip: "Slip",
            prachar: "Prachar",
            print: "Print",
            family: "Family",
            foundLabel: "members found",
            removeFromGroup: "Remove" // Added translation
        },
        hi: {
            pageTitle: "मेरा समूह",
            pageSubtitle: "आपके व्यक्तिगत समूह में शामिल सदस्यों की सूची।",
            filterPlaceholder: "नाम से प्रदर्शित परिणाम फ़िल्टर करें...",
            loadingData: "डेटा लोड हो रहा है...",
            noMembers: "आपके समूह में कोई सदस्य नहीं मिला।",
            noMatching: "मेल खाने वाले कोई नाम नहीं मिले।",
            fatherHusband: "पिता/पति :",
            serialNo: "क्रमांक :",
            mobile: "मोबाइल :",
            idCardNo: "पहचान पत्र नं.:",
            boothNo: "बूथ नं :",
            houseNo: "घर नं :",
            pollingLoc: "मतदान स्थान :",
            call: "कॉल",
            text: "मैसेज",
            slip: "पर्ची",
            prachar: "प्रचार",
            print: "प्रिंट",
            family: "परिवार",
            foundLabel: "सदस्य मिले",
            removeFromGroup: "हटाएं" // Added translation
        },
        gu: {
            pageTitle: "મારું જૂથ",
            pageSubtitle: "તમારા વ્યક્તિગત જૂથમાં સોંપાયેલ સભ્યોની યાદી.",
            filterPlaceholder: "જૂથના સભ્યોને નામથી ફિલ્ટર કરો...",
            loadingData: "ડેટા લોડ થઈ રહ્યો છે...",
            noMembers: "તમારા જૂથમાં કોઈ સભ્ય મળ્યા નથી.",
            noMatching: "કોઈ મેળ ખાતું નામ મળ્યું નથી.",
            fatherHusband: "પિતા/પતિ :",
            serialNo: "ક્રમાંક :",
            mobile: "મોબાઇલ :",
            idCardNo: "ઓળખ કાર્ડ નં :",
            boothNo: "બૂથ નં :",
            houseNo: "ઘર નં :",
            pollingLoc: "મતદાન સ્થળ :",
            call: "કૉલ",
            text: "સંદેશ",
            slip: "સ્લિપ",
            prachar: "પ્રચાર",
            print: "પ્રિન્ટ",
            family: "કુટુંબ",
            foundLabel: "સભ્યો મળ્યા",
            removeFromGroup: "દૂર કરો" // Added translation
        }
    };

    const txt = t[lang];
    const location = useLocation();
    const navigate = useNavigate();

    // Retrieve userId passed from the previous page
    const passedUserId = location.state?.userId;
    const targetUserId = passedUserId || userId;

    // --- API Fetch Function ---
    const fetchGroupMembers = async () => {
        if (!appId || !userId) return;
        setLoading(true);
        setCardSearchTerm("");

        try {
            const response = await axios.post(
                `${BASE_URL}/api/dis_my_group_member`,
                {
                    app_id: appId,
                    user_id: targetUserId
                }
            );

            if (response.data.success && Array.isArray(response.data.data.result)) {
                setMembers(response.data.data.result);
            } else {
                setMembers([]);
            }
        } catch (error) {
            console.error("Error fetching group members:", error);
            setMembers([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!appId) {
            navigate("/");
            return;
        }
        fetchGroupMembers();
    }, [appId, userId]);

    // --- Filter Logic ---
    const filteredMembers = members.filter((member) => {
        if (!cardSearchTerm) return true;
        const searchTerm = cardSearchTerm.toLowerCase().trim();
        const fName = member.eng_f_name ? member.eng_f_name.toLowerCase() : "";
        const mName = member.eng_m_name ? member.eng_m_name.toLowerCase() : "";
        const sName = member.eng_surname ? member.eng_surname.toLowerCase() : "";
        const fullName = `${fName} ${mName} ${sName}`.replace(/\s+/g, ' ').trim();
        const shortName = `${fName} ${sName}`.replace(/\s+/g, ' ').trim();
        const mobile = member.contact_no ? String(member.contact_no).toLowerCase() : "";

        return fullName.includes(searchTerm) || shortName.includes(searchTerm) || mobile.includes(searchTerm);
    });

    // --- Remove from Group API Call ---
    const handleRemoveFromGroup = async (member) => {
        if (window.confirm("Are you sure you want to remove this member from your group?")) {
            try {
                const payload = {
                    id: member.member_id
                };
                console.log(member);
                const response = await axios.post(`${BASE_URL}/api/del_my_group_member`, payload);

                if (response.data.success) {
                    alert("Removed from group successfully!");
                    fetchGroupMembers(); // Re-fetch list to update UI
                } else {
                    alert("Failed to remove from group.");
                }
            } catch (error) {
                console.error("Error removing from group:", error);
                alert("An error occurred while removing from the group.");
            }
        }
    };

    // --- WhatsApp Message Generation Logic ---
    const handleWhatsAppShare = async (member) => {
        if (!member.contact_no || member.contact_no === "-") {
            alert("Mobile number not available.");
            return;
        }

        try {
            const response = await axios.post(`${BASE_URL}/api/get_receipt_in_all_family_for_wtsp`, {
                app_id: appId,
                voter_id: member.id,
                user_id: appId
            });

            if (response.data.success && response.data.data && response.data.data.result) {
                const familyMembers = response.data.data.result;
                let message = "";

                familyMembers.forEach((fam, index) => {
                    message += `${fam.first_detail.replace(/\s+/g, ' ')}\n`;
                    message += `${fam.full_name}\n`;
                    message += `${fam.middle_name}\n`;
                    message += `${fam.sex_age.replace('स्री', 'स्त्री')}\n`;
                    message += `${fam.idcard_no}\n\n`;
                    message += `मतदान स्थल :\n`;
                    message += `${fam.eng_polling_location}\n\n`;

                    if (index < familyMembers.length - 1) {
                        message += `----------------------------------------\n\n`;
                    }
                });

                const encodedMessage = encodeURIComponent(message);
                let mobile = member.contact_no.replace(/\D/g, '');
                if (mobile.length === 10) mobile = '91' + mobile;
                window.open(`https://wa.me/${mobile}?text=${encodedMessage}`, '_blank');
            } else {
                alert("Could not fetch family details.");
            }
        } catch (error) {
            console.error("WhatsApp error:", error);
            alert("Error generating message.");
        }
    };

    return (
        <div className='app-container p-4'>

            <Header
                title={txt.pageTitle}
            />

            {/* Professional Filter Container (Always Visible) */}
            <div className="filter-container-modern">
                <div className="search-wrapper-modern">
                    <svg className="search-icon-modern" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <input
                        className="search-input-modern"
                        type="text"
                        placeholder={txt.filterPlaceholder}
                        value={cardSearchTerm}
                        onChange={(e) => setCardSearchTerm(e.target.value)}
                    />
                </div>
            </div>

            {loading && (
                <div className="loader-wrapper">
                    <div className="spinner"></div>
                    <p className="loader-text">{txt.loadingData}</p>
                </div>
            )}

            {!loading && filteredMembers.length === 0 && (
                <div className="text-center text-muted p-5">
                    {members.length === 0 ? txt.noMembers : txt.noMatching}
                </div>
            )}

            {/* --- GRID SYSTEM --- */}
            <div className="row g-4">
                {!loading && filteredMembers.map((member, index) => (
                    <div className="col-12 col-md-6 col-lg-4" key={member.id || index}>
                        <div className="voter-card-clean h-100">
                            <div className="voter-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div>
                                    <span className="serial-number">{index + 1}.</span>
                                    <span className="voter-name-bold ms-2">
                                        {member.eng_f_name || ""} {member.eng_surname || ""}
                                    </span>
                                </div>
                                <button 
                                    onClick={() => handleRemoveFromGroup(member)}
                                    style={{ 
                                        backgroundColor: '#fef2f2', 
                                        color: '#ef4444', 
                                        border: '1px solid #fecaca', 
                                        borderRadius: '20px', 
                                        padding: '4px 10px', 
                                        fontSize: '12px', 
                                        fontWeight: '600',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease'
                                    }}
                                    onMouseOver={(e) => { e.target.style.backgroundColor = '#fee2e2'; }}
                                    onMouseOut={(e) => { e.target.style.backgroundColor = '#fef2f2'; }}
                                >
                                    - {txt.removeFromGroup}
                                </button>
                            </div>

                            <div className="voter-body position-relative">
                                <div className="map-icon-wrapper">
                                    <img src="https://upload.wikimedia.org/wikipedia/commons/a/aa/Google_Maps_icon_%282020%29.svg" alt="Map" width="15" />
                                </div>
                                <div className="info-row"><span className="label">{txt.fatherHusband}</span><span className="value">{member.eng_m_name || "N/A"}</span></div>
                                <div className="info-row"><span className="label">{txt.serialNo}</span><span className="value">{member.slnoinpart || index + 1}</span></div>
                                <div className="info-row"><span className="label">{txt.mobile}</span><span className="value mobile-value">{member.contact_no || "N/A"}</span></div>
                                <div className="info-row"><span className="label">{txt.idCardNo}</span><span className="value">{member.idcard_no || "N/A"}</span></div>
                                <div className="info-row"><span className="label">{txt.boothNo}</span><span className="value">{member.part_no || "N/A"}</span></div>
                                <div className="info-row"><span className="label">{txt.houseNo}</span><span className="value">{member.eng_house_no || "N/A"}</span></div>
                                <div className="info-row"><span className="label">{txt.pollingLoc}</span><span className="value">{member.eng_polling_location || "-"}</span></div>
                            </div>

                            <div className="voter-actions">
                                <ActionButton icon="📞" color="#0ea5e9" label={txt.call} link={`tel:${member.contact_no}`} />
                                <ActionButton icon="✉️" color="#f59e0b" label={txt.text} link={`sms:${member.contact_no}`} />
                                <button
                                    onClick={() => handleWhatsAppShare(member)}
                                    className="action-btn-wrapper"
                                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                                >
                                    <div className="circle-btn" style={{ backgroundColor: '#22c55e' }}>💬</div>
                                    <span className="btn-label">{txt.slip}</span>
                                </button>
                                <ActionButton icon="👤" color="#ef4444" label={txt.prachar} />
                                {/* Updated Print Button with Navigation */}
                                <ActionButton
                                    icon="🖨"
                                    color="#f97316"
                                    label={txt.print}
                                    onClick={() => navigate('/printer', { state: { voter_id: member.id } })}
                                />
                                <ActionButton icon="👨‍👩‍👧" color="#fbbf24" label={txt.family} />
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <style jsx>{`
                /* --- PROFESSIONAL FILTER BAR --- */
                .filter-container-modern {
                    display: flex;
                    justify-content: flex-start;
                    align-items: center;
                    margin-bottom: 24px;
                }
                .search-wrapper-modern {
                    position: relative;
                    width: 100%;
                    max-width: 450px;
                }
                .search-icon-modern {
                    position: absolute;
                    left: 16px;
                    top: 50%;
                    transform: translateY(-50%);
                    color: #94a3b8;
                    width: 18px;
                    height: 18px;
                }
                .search-input-modern {
                    width: 100%;
                    padding: 12px 16px 12px 46px;
                    border: 1px solid #e2e8f0;
                    border-radius: 12px;
                    font-size: 14px;
                    color: #1e293b;
                    outline: none;
                    transition: all 0.2s ease;
                    box-shadow: 0 1px 2px rgba(0,0,0,0.05);
                }
                .search-input-modern:focus {
                    border-color: #2563eb;
                    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
                }

                /* --- LOADER --- */
                .loader-wrapper { display: flex; flex-direction: column; justify-content: center; align-items: center; min-height: 50vh; width: 100%; }
                .spinner { width: 40px; height: 40px; border: 4px solid #E5E7EB; border-top: 4px solid #2563EB; border-radius: 50%; animation: spin 1s linear infinite; margin-bottom: 12px; }
                @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }

                @media (max-width: 640px) {
                    .search-wrapper-modern { max-width: 100%; }
                }
            `}</style>
        </div>
    );
};

// Updated ActionButton to accept onClick props
const ActionButton = ({ icon, color, label, link, onClick }) => {
    if (onClick) {
        return (
            <button onClick={onClick} className="action-btn-wrapper" style={{ textDecoration: 'none', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                <div className="circle-btn" style={{ backgroundColor: color }}>{icon}</div>
                <span className="btn-label">{label}</span>
            </button>
        );
    }
    
    return (
        <a href={link || "#"} className="action-btn-wrapper" style={{ textDecoration: 'none' }} target="_blank" rel="noreferrer">
            <div className="circle-btn" style={{ backgroundColor: color }}>{icon}</div>
            <span className="btn-label">{label}</span>
        </a>
    );
};

export default MyGroup;